import React from 'react';
import { Setup } from '../types';
import { ArrowRight, Check } from 'lucide-react';
import { ApiService } from '../utils/api';

interface SetupCardProps {
  setup: Setup;
  benefits: string[];
}

const SetupCard: React.FC<SetupCardProps> = ({ setup, benefits }) => {
  const levelColors = {
    beginner: 'from-green-500 to-emerald-600',
    enthusiast: 'from-blue-500 to-indigo-600',
    audiophile: 'from-purple-500 to-pink-600',
  };

  const levelBadgeColors = {
    beginner: 'bg-green-100 text-green-800 border-green-200',
    enthusiast: 'bg-blue-100 text-blue-800 border-blue-200',
    audiophile: 'bg-purple-100 text-purple-800 border-purple-200',
  };

  return (
    <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden card-hover">
      {/* Header with gradient */}
      <div className={`h-32 bg-gradient-to-r ${levelColors[setup.level]} relative`}>
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative p-6 h-full flex items-center justify-between">
          <div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${levelBadgeColors[setup.level]}`}>
              {setup.level.charAt(0).toUpperCase() + setup.level.slice(1)}
            </span>
            <h3 className="text-2xl font-bold text-white mt-2">{setup.name}</h3>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-white">
              {ApiService.formatPrice(setup.totalPrice)}
            </div>
            <div className="text-white/80 text-sm">Total Budget</div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <p className="text-gray-600 mb-6">{setup.description}</p>

        {/* Products */}
        <div className="space-y-3 mb-6">
          <h4 className="font-semibold text-gray-900 mb-3">Components:</h4>
          
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Speakers</span>
            <span className="font-medium text-gray-900">{setup.products.speaker}</span>
          </div>
          
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Turntable</span>
            <span className="font-medium text-gray-900">{setup.products.turntable}</span>
          </div>
          
          {setup.products.amplifier && (
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Amplifier</span>
              <span className="font-medium text-gray-900">{setup.products.amplifier}</span>
            </div>
          )}
        </div>

        {/* Benefits */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-900 mb-3">Key Benefits:</h4>
          <ul className="space-y-2">
            {benefits.slice(0, 3).map((benefit, index) => (
              <li key={index} className="flex items-center text-sm text-gray-600">
                <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        {/* CTA Button */}
        <button className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center group">
          View Setup Details
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
    </div>
  );
};

export default SetupCard;